import { useMemo } from 'react'
import { NestAPI } from '@marketing-milk/frontend'
import { CreativeReviewTrail } from '@marketing-milk/interfaces'
import { useQuery } from 'react-query'

type Body<T> = { body: T }

/**
 * Prefix for every packet's trails. A verdict or an edit knows its creative but
 * not which packet shows it, so the mutations invalidate on this.
 */
export const packetReviewTrailsQueryPrefix = ['packet-review-trails'] as const

/**
 * Every ad's review trail on one packet (DRI-190): what feeds "Edited after
 * review" and the line under each card. Readable by clients, who see the same
 * trail. Keyed by creative id; an ad the server has no trail for simply has none.
 */
export const usePacketReviewTrails = (businessID: number, packetID: number | undefined) => {
  const { data, isLoading } = useQuery(
    [...packetReviewTrailsQueryPrefix, businessID, packetID],
    async () => {
      const response = await NestAPI.get<Body<CreativeReviewTrail[]>>(
        `businesses/${businessID}/sprint-packets/${packetID}/review-trails`
      )
      return response.data.body
    },
    {
      enabled: typeof packetID === 'number' && !Number.isNaN(packetID),
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    }
  )

  const trails = useMemo(
    () => new Map((data ?? []).map(trail => [trail.creativeID, trail])),
    [data]
  )

  return { trails, isLoading }
}
